import React, { useEffect, useMemo, useState } from 'react';
import { TrendingUp, TrendingDown, Minus, BarChart3 } from 'lucide-react';
import clsx from 'clsx';
import {
  ResponsiveContainer,
  AreaChart,
  Area,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
} from 'recharts';
import { facebookInsightsService, InsightsTrendPoint } from '@/services/facebook-insights.service';

interface Props {
  campaignId?: string;
  /** Days back from today to include. Defaults to 30. */
  days?: number;
  currency?: string;
}

type Metric = 'spend' | 'impressions' | 'clicks' | 'reach' | 'conversions';

const METRICS: { key: Metric; label: string; color: string }[] = [
  { key: 'spend',       label: 'Spend',       color: '#f59e0b' },
  { key: 'impressions', label: 'Impressions', color: '#3b82f6' },
  { key: 'clicks',      label: 'Clicks',      color: '#10b981' },
  { key: 'reach',       label: 'Reach',       color: '#8b5cf6' },
  { key: 'conversions', label: 'Conversions', color: '#ef4444' },
];

function ymd(d: Date): string {
  return d.toISOString().slice(0, 10);
}

function formatValue(metric: Metric, n: number, currency?: string): string {
  if (metric === 'spend') {
    return `${currency ?? ''} ${n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`.trim();
  }
  return n.toLocaleString();
}

function delta(a: number, b: number): number | null {
  if (b === 0) return null;
  return ((a - b) / b) * 100;
}

const MetricChart: React.FC<{
  metric: Metric;
  label: string;
  color: string;
  data: InsightsTrendPoint[];
  currency?: string;
}> = ({ metric, label, color, data, currency }) => {
  const total = data.reduce((s, p) => s + p[metric], 0);
  const half = Math.floor(data.length / 2);
  const recent = data.slice(half).reduce((s, p) => s + p[metric], 0);
  const prior  = data.slice(0, half).reduce((s, p) => s + p[metric], 0);
  const d = delta(recent, prior);
  const gradId = `grad-${metric}`;

  return (
    <div className="rounded-xl border border-border bg-surface p-3">
      <div className="mb-1 flex items-center justify-between">
        <span className="text-2xs font-semibold uppercase tracking-[0.12em] text-text-muted">{label}</span>
        {d !== null && (
          <span className={clsx(
            'inline-flex items-center gap-0.5 rounded-full px-1.5 py-0.5 text-[10px] font-semibold',
            d > 0.5  ? 'bg-success-soft text-success' :
            d < -0.5 ? 'bg-danger-soft text-danger' :
                       'bg-surface-alt text-text-muted',
          )}>
            {d > 0.5 ? <TrendingUp size={9} /> : d < -0.5 ? <TrendingDown size={9} /> : <Minus size={9} />}
            {d > 0 ? '+' : ''}{d.toFixed(1)}%
          </span>
        )}
      </div>
      <div className="mb-2 font-display text-lg font-bold text-text-primary tabular-nums">
        {formatValue(metric, total, currency)}
      </div>
      <div style={{ width: '100%', height: 100 }}>
        <ResponsiveContainer width="100%" height="100%">
          <AreaChart data={data} margin={{ top: 4, right: 4, left: 0, bottom: 0 }}>
            <defs>
              <linearGradient id={gradId} x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%"   stopColor={color} stopOpacity={0.4} />
                <stop offset="100%" stopColor={color} stopOpacity={0.02} />
              </linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="3 3" stroke="rgba(120,120,120,0.12)" vertical={false} />
            <XAxis
              dataKey="date"
              tick={{ fontSize: 9, fill: 'currentColor' }}
              tickFormatter={(v: string) => v.slice(5)}
              interval="preserveStartEnd"
              minTickGap={20}
              axisLine={false}
              tickLine={false}
            />
            <YAxis hide domain={[0, 'dataMax']} />
            <Tooltip
              cursor={{ stroke: color, strokeOpacity: 0.2 }}
              contentStyle={{
                background: 'var(--surface, #fff)',
                border: '1px solid rgba(120,120,120,0.2)',
                borderRadius: 8,
                fontSize: 11,
                padding: '6px 8px',
              }}
              labelStyle={{ fontSize: 10, color: '#888' }}
              formatter={(v) => [formatValue(metric, Number(v), currency), label] as [string, string]}
            />
            <Area
              type="monotone"
              dataKey={metric}
              stroke={color}
              strokeWidth={2}
              fill={`url(#${gradId})`}
              isAnimationActive={false}
            />
          </AreaChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
};

export const InsightsTrendStrip: React.FC<Props> = ({ campaignId, days = 30, currency }) => {
  const [data, setData] = useState<InsightsTrendPoint[] | null>(null);

  useEffect(() => {
    if (!campaignId) { setData([]); return; }
    const until = ymd(new Date());
    const since = ymd(new Date(Date.now() - days * 86400_000));
    facebookInsightsService.campaignTrend(campaignId, since, until)
      .then(setData)
      .catch(() => setData([]));
  }, [campaignId, days]);

  const dateRange = useMemo(() => {
    if (!data || data.length === 0) return null;
    return { from: data[0]?.date, to: data[data.length - 1]?.date };
  }, [data]);

  if (data === null) {
    return (
      <div className="rounded-2xl border border-border bg-surface p-5">
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {[...Array(5)].map((_, i) => <div key={i} className="skeleton h-36 rounded-xl" />)}
        </div>
      </div>
    );
  }

  if (data.length === 0) {
    return (
      <div className="rounded-2xl border border-dashed border-border bg-surface-alt/40 px-5 py-6 text-center">
        <BarChart3 size={24} className="mx-auto mb-2 text-text-muted" />
        <p className="text-sm font-semibold text-text-primary">No historical snapshots yet</p>
        <p className="mt-1 text-xs text-text-muted">
          Snapshots are written daily at 04:30 AM (UTC). Your first data point will appear tomorrow morning;
          the trend chart fills in over time as more days accumulate.
        </p>
      </div>
    );
  }

  return (
    <div className="rounded-2xl border border-border bg-surface p-4">
      <div className="mb-3 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <BarChart3 size={14} className="text-primary" />
          <span className="text-xs font-semibold text-text-primary">Last {days} days · Performance trends</span>
        </div>
        {dateRange && (
          <span className="text-2xs text-text-muted tabular-nums">
            {dateRange.from} → {dateRange.to}
          </span>
        )}
      </div>

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
        {METRICS.map(({ key, label, color }) => (
          <MetricChart
            key={key}
            metric={key}
            label={label}
            color={color}
            data={data}
            currency={currency}
          />
        ))}
      </div>
    </div>
  );
};
